import React, { useCallback, useContext, useState } from 'react';
import {
  DataRoomMember,
  DataRoomMembers,
} from 'router/subrouters/DataRoom/models/DataRoomMember';
import { DocumentsAvailabilityDrawer } from 'router/subrouters/DataRoom/components/DocumentAvailabilityDrawer';
import {
  MEMBER_DRAWER_OPTIONS,
  MemberDrawerOption,
} from 'router/subrouters/DataRoom/components/DocumentAvailabilityDrawer/data';
import {
  DrawerNavigation,
  SideDrawerRef,
} from 'router/subrouters/DataRoom/components/SideDrawer/SideDrawer.component';
import useCallbackRef from 'hooks/useCallbackRef';
import useDeleteMember from 'router/subrouters/DataRoom/hooks/useDeleteMember';
import MembersContext from 'router/subrouters/DataRoom/providers/Members/Members.context';
import dataRoomApi from 'router/subrouters/DataRoom/dataRoomApi';
import { ManageRequests } from '../components/ManageRequests';
import useMemberRequestAccess from '../../../hooks/useMemberRequestAccess';

type UseMemberDrawersProps = {
  isBuySide?: boolean;
};

export default function useMemberDrawers({
  isBuySide = false,
}: UseMemberDrawersProps = {}) {
  const [selectedMember, setSelectedMember] = useState<DataRoomMember>();
  const [drawerTab, setDrawerTab] = useState<MemberDrawerOption>(
    MEMBER_DRAWER_OPTIONS.SETTINGS,
  );

  const {
    setBuySideGroups,
    setSellSideMembers,
    updateMember: updateMemberContext,
  } = useContext(MembersContext);

  const [manageDocuments, manageDocumentsRef] =
    useCallbackRef<SideDrawerRef>(null);
  const [manageRequests, manageRequestsRef] =
    useCallbackRef<SideDrawerRef>(null);

  const updateDataRoomMembers = useCallback(
    ({ buySideGroups, sellSideMembers }: DataRoomMembers) => {
      setBuySideGroups(buySideGroups);
      setSellSideMembers(sellSideMembers);
    },
    [setBuySideGroups, setSellSideMembers],
  );

  const onEdit = useCallback(
    (member: DataRoomMember, tab = MEMBER_DRAWER_OPTIONS.SETTINGS) => {
      setSelectedMember(member);

      // Members still waiting on their access request have no settings or
      // activity to show yet, so every entry point leads to the request drawer.
      if (member?.requestedDataRoomAccess) {
        manageDocuments?.setOpen(false);
        manageRequests?.setOpen(true);
      } else {
        manageRequests?.setOpen(false);
        setDrawerTab(tab);
        manageDocuments?.setOpen(true);
      }
    },
    [manageDocuments, manageRequests],
  );

  const updateMember = useCallback(
    (updatedMember: Partial<DataRoomMember>) => {
      updateMemberContext(updatedMember);
      setSelectedMember((old) => ({ ...old, ...updatedMember }));
    },
    [updateMemberContext],
  );

  const onEditMemberRole = useCallback(
    (memberId: string, updated: Partial<DataRoomMember>) => {
      dataRoomApi.members.updateMember(memberId, updated);
      updateMember({ _id: memberId, ...updated });
    },
    [updateMember],
  );

  // Reviewing a request takes it out of the queue and off the members table,
  // and `useMemberRequestAccess` owns both of those. All that is left here is
  // dropping the member the drawer was pointed at.
  const clearSelectedMember = useCallback(() => setSelectedMember(null), []);

  const { acceptRequest, rejectRequest } = useMemberRequestAccess();

  // Both review actions also sit in the row menu, where no drawer is open and
  // there is no selected member to keep in step.
  const onAcceptRequest = useCallback(
    async (member: DataRoomMember) => acceptRequest(member),
    [acceptRequest],
  );

  const onRejectRequest = useCallback(
    async ({ _id }: DataRoomMember) => rejectRequest(_id, clearSelectedMember),
    [clearSelectedMember, rejectRequest],
  );

  const deleteMember = useDeleteMember();

  // Deleting from the row menu skips the drawer entirely: the confirmation is
  // the only thing that opens.
  const onDeleteMember = useCallback(
    (member: DataRoomMember) =>
      deleteMember({ member, onSuccess: updateDataRoomMembers }),
    [deleteMember, updateDataRoomMembers],
  );

  const renderDrawers = useCallback(
    ({ onPrev, onNext }: DrawerNavigation) => (
      <>
        <DocumentsAvailabilityDrawer
          ref={manageDocumentsRef}
          isBuySide={isBuySide}
          member={selectedMember}
          defaultOpenRoles
          hasAdditionalActions
          defaultTab={drawerTab}
          updateMember={updateMember}
          updateDataRoomMembers={updateDataRoomMembers}
          onPrev={onPrev}
          onNext={onNext}
        />

        <ManageRequests
          ref={manageRequestsRef}
          isBuySide={isBuySide}
          member={selectedMember}
          updateMember={updateMember}
          removeMember={clearSelectedMember}
          onPrev={onPrev}
          onNext={onNext}
        />
      </>
    ),
    [
      manageDocumentsRef,
      manageRequestsRef,
      isBuySide,
      selectedMember,
      drawerTab,
      updateMember,
      updateDataRoomMembers,
      clearSelectedMember,
    ],
  );

  return {
    selectedMemberId: selectedMember?._id,
    onEdit,
    onEditMemberRole,
    onDeleteMember,
    onAcceptRequest,
    onRejectRequest,
    renderDrawers,
  };
}
